//core
import React, {useCallback, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "usehooks-ts";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
} from "@mui/material";

//styles
import { useHeaderStyles } from "../../../../UI/PageHeaderStyles/styles";

//context
import { GlobalContext, OrderType } from "../../../../../context/GlobalContext";

//routes
import routes from "../../../../../navigation/routes";

//selectors
import {
	selectListData,
	selectListError,
	selectDeleteData, selectCreateData, selectUpdateData, selectDownloadData, selectCreatePayoutData,
	selectUpdatePayoutData, selectRemovePayoutData,
} from "../../../../../store/admin/flatFee/selectors";

//hooks
import useDidMountEffect from "../../../../../customHooks/useDidMountEffect";
import { useFlatFee } from "../../../../../store/admin/flatFee/useFlatFee";

//helpers
import { getIds, getObjIds } from "../../../../../helpers/getIds";
import { getCookie } from "../../../../../helpers/utils";

//constants
import { cookieToken } from "../../../../../constants";

//components
import { usePermissions } from "../../../../../store/common/configuration/usePermissions";
import TableActions from "../TableActions";
import TableData from "../../../../common/TableData";
import ErrorData from "../../../../common/ErrorData";
import PermissionsWrapper from "../../../../common/PermissionsWrapper";
import DrawerFilter from "../DrawerFilter";
import MobileList from "./components/MobileList";
import { getDataIds } from "../../../Reports/utils";
import format from "date-fns/format";
import { IFlatFeeFilterDataType } from "../../types";
import MuiModal from "../../../../UI/MuiModal";
import DownloadReport from "../DownloadReport";
import Payouts from "../Payouts";
import { PayoutsModalContext } from "./PayoutsModalContext";

export const initFlatFeeFilterState: IFlatFeeFilterDataType = {
	sent_from: '',
	sent_to: '',
	trackerIds: [],
	brandCodes: [],
	affiliateIds: [],
	statuses: [],
	brandCurrencies: [],
}

interface IDesktopProps {
  // search: string;
}

const filterData = {
	separators_for_format: {
		"csv": ["comma", "semicolon", "pipeline", "tab"]
	},
	encoding_types: ["Unicode, 8-bit", "Windows CP1251", "ASCII"],
	columns: ["id", "status", "amount", "brand", "brand_currency", "affiliate_id", "tracker_id", "description", "target_date", "created_at", "created_by", "modified_at", "modified_by"]
}

const Desktop: React.FC<IDesktopProps> = ({
  // search,
}): JSX.Element => {
  const { t } = useTranslation();
  const headerStyles = useHeaderStyles();
  const history = useNavigate();
  const { hasPermissions } = usePermissions();

  const mobile = useMediaQuery('(max-width:767px)');

  const { getList, clearList, clearDelete, setDownload, clearDownloadState, } = useFlatFee();

  const context = useContext(GlobalContext);

  const data = useSelector(selectListData);
  const error = useSelector(selectListError);
  const deleteData = useSelector(selectDeleteData);
	const createData = useSelector(selectCreateData);
	const updateData = useSelector(selectUpdateData);
	const downloadData = useSelector(selectDownloadData);
	const createdNewPayout = useSelector(selectCreatePayoutData);
	const updatePayout = useSelector(selectUpdatePayoutData);
	const removePayout = useSelector(selectRemovePayoutData);

	const [generateReport, setGenerateReport] = useState<boolean>(false);
	const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
	const [showPayoutsModal, setShowPayoutsModal] = useState<boolean>(false);
	const [payoutsData, setPayoutsData] = useState<any>(null);

  const [filter, setFilter] = useState<IFlatFeeFilterDataType>(context.admFlatFeeFilter);

  const [order, setOrder] = useState<OrderType>(context.admFlatFeeOrder);
  const [page, setPage] = useState<number>(context.admFlatFeePage);
  const [rowsPerPage, setRowsPerPage] = useState(context.admFlatFeeRows);

  const debouncedFilter = useDebounce<IFlatFeeFilterDataType>(filter, 150);

  const [actionsCount, setActionsCount] = useState<number>(0);

  const hasDelete = hasPermissions(["api3.admin.flatfeecontroller.annulate"]);
	const hasUpdate = hasPermissions(["api3.admin.flatfeecontroller.edit"]);
	const isPermissionShowPayment = hasPermissions(["api3.admin.flatfeecontroller.payment_create"]);

  /* action calculation */
  useEffect(() => {
    if (hasDelete) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [hasDelete]);
	useEffect(() => {
    if (hasUpdate) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [hasUpdate]);
	useEffect(() => {
    if (isPermissionShowPayment) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [isPermissionShowPayment]);
  /* end action calculation */

  useDidMountEffect(() => {
    setPage(0);
    context.setAdmFlatFeePage(0);
    const data = getPayloadData(1);

    getList(data);
  }, [
		// search,
	  order, rowsPerPage, debouncedFilter]);

  useEffect(() => {
    context.setAdmFlatFeePage(page);
    const data = getPayloadData(page + 1)
    getList(data);
  }, [page]);

  useEffect(() => {
    return () => {
      clearList();
	    clearDelete();
    };
  }, []);

  useDidMountEffect(() => {
    context.setAdmFlatFeeFilter(filter);
  },[filter]);

  useDidMountEffect(() => {
    if (!!deleteData || !!createData || !!updateData || createdNewPayout || updatePayout || removePayout) {
      const payload = getPayloadData(page + 1);
      getList(payload);
    }
  }, [deleteData, createData, updateData, createdNewPayout, updatePayout, removePayout]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
    context.setAdmFlatFeePage(newPage);
  }, [setPage]);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    context.setAdmFlatFeeRows(+event.target.value);
  }, [setRowsPerPage, setPage]);

  const getPayloadData = (page: number) => {
    return {
      page: page,
      perPage: rowsPerPage,
      order: order,
      // search: search ? search : null,
      filters: {
	      ...(!!filter?.sent_from ? {periodStart: format(new Date(filter.sent_from), 'yyyy-MM-dd')} : {}),
	      ...(!!filter?.sent_to ? {periodEnd: format(new Date(filter.sent_to), 'yyyy-MM-dd')} : {}),
	      trackerIds: !!filter?.trackerIds ? getDataIds(filter.trackerIds) : [],
	      brandCodes: !!filter?.brandCodes ? getDataIds(filter.brandCodes) : [],
	      affiliateIds: !!filter?.affiliateIds ? getDataIds(filter.affiliateIds) : [],
	      statuses: !!filter?.statuses ? getDataIds(filter.statuses) : [],
        ...(!!filter?.brandCurrencies.length ? { brandCurrencies: getDataIds(filter.brandCurrencies) } : {}),
      },
      authenticity_token: getCookie(cookieToken) || null
    }
  };

  const handleGenerateWithCurrentState = useCallback(() => {
    const payload = getPayloadData(page + 1);
    getList(payload);
  }, [page, getPayloadData]);

  const handleChangeOrder = (newOrder: OrderType) => {
    setPage(0);
    context.setAdmFlatFeePage(0);
    context.setAdmFlatFeeOrder(newOrder);
    setOrder(newOrder);
  };

	const handleDownloadReport = useCallback(() => {
		setShowDownloadModal(true);
	}, [setShowDownloadModal]);

	const openPayoutsModal = useCallback((rowData: any) => {
		setPayoutsData(rowData);
		setShowPayoutsModal(true);
	}, []);

	const togglePayoutsModal = useCallback((state: boolean) => {
		setShowPayoutsModal(state);
		if (!state) {
			setPayoutsData(null);
			const payload = getPayloadData(page + 1);
			getList(payload);
		}
	}, [page, rowsPerPage, order, filter]);

  return (
    <PayoutsModalContext.Provider value={{ openPayoutsModal }}>
    <Box>
      <DrawerFilter
	      initialFilter={filter}
	      setUpdateFilter={setFilter}
	      order={order}
	      setOrder={setOrder}
	      onGenerateReport={handleGenerateWithCurrentState}
	      setGenerateReport={setGenerateReport}
	      onDownload={handleDownloadReport}
      >
        {!mobile && (
          <Typography className={headerStyles.pageTitle} variant="h6">{t("admin.flatFee.title")}</Typography>
        )}
      </DrawerFilter>

      <Box className={headerStyles.contentWrapper}>
        {!!data && (<>
          {mobile ? (
            <MobileList
              data={data}
              currentPage={page}
              setPage={handleChangePage}
            />
          ) : (
            <TableData
              list={data.list}
              columns={data.columns}
              order={order}
              setOrder={setOrder}
              page={page}
              rowsPerPage={rowsPerPage}
              totalEntries={+data.totalEntries}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              actionsCount={!!actionsCount ? actionsCount : 1}
              {...(!!actionsCount ? { ActionsComponent: TableActions } : {})}
              translationColumnsPrefix="columns_for_display.table.flatFee.columns."
              startRowNum={data.startRowNum}
              endRowNum={data.endRowNum}
            />
          )}
        </>)}
      </Box>

      {
        error && <ErrorData title={error?.status} text={error?.detail} />
      }

	    <MuiModal
		    open={showDownloadModal}
		    closeOnOutside={false}
		    setToggleModal={setShowDownloadModal}
	    >
		    {!!filterData && !!filter && (
			    <DownloadReport
				    order={order}
				    downloadOnThisPage={false}
				    downloadData={downloadData}
				    encodingTypes={filterData.encoding_types}
				    separatorsForFormat={filterData.separators_for_format}
				    filter={filter}
				    setToggleModal={setShowDownloadModal}
				    setDownload={setDownload}
				    clearDownloadState={clearDownloadState}
				    list={data?.list}
			    />
		    )}
	    </MuiModal>

	    <MuiModal
		    width={1032}
		    open={showPayoutsModal}
		    closeOnOutside={false}
		    setToggleModal={togglePayoutsModal}
	    >
		    {!!payoutsData && (
			    <Payouts
				    paymentData={payoutsData}
				    affiliateId={payoutsData.affiliate_id}
			    />
		    )}
	    </MuiModal>
    </Box>
    </PayoutsModalContext.Provider>
  );
};

export default Desktop;
